import * as React from 'react'
import { Accordion, AccordionDetails } from '@mui/material'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { styled as muiStyled } from '@mui/system'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import Styles from './CostBreakdownAccordion.module.css'
import Colors from 'Theme/Colors'
import IFText from 'Components/IFText/IFText'

const AccordionSummary = muiStyled(MuiAccordionSummary)(() => ({
  minHeight: '2.5rem',
  '&.Mui-expanded': {
    minHeight: '2.5rem',
  },
  '& .MuiAccordionSummary-content': {
    margin: '0px',
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: '0px',
  },
}))

const CostBreakdownAccordion = ({ costBreakdown, currency }) => {
  const { t } = useTranslation()

  if (!costBreakdown) return null

  const decimals = currency?.decimalPlaces ?? 2
  const formatAmount = (value) =>
    value == null ? '-' : Number(value).toFixed(decimals)

  const items = costBreakdown.items ?? []
  const { subtotal, vatTotal, total } = costBreakdown

  const typeLabelKeys = {
    ENERGY: 'CostBreakdown.TypeEnergy',
    TIME: 'CostBreakdown.TypeTime',
    PARKING_TIME: 'CostBreakdown.TypeParkingTime',
    FLAT: 'CostBreakdown.TypeFlat',
  }
  const getTypeLabel = (type) => {
    const key = typeLabelKeys[type?.toUpperCase?.()]
    return key ? t(key) : type
  }

  return (
    <Accordion
      disableGutters
      className={Styles.accordion}
      sx={{
        bgcolor: 'transparent',
        color: Colors.text,
        boxShadow: 'none',
        border: `1px solid ${Colors.DividerColor}`,
        borderRadius: '5px',
        '&:before': { display: 'none' },
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <IFText className={Styles.title}>
          {t('CostBreakdown.Title')}
        </IFText>
      </AccordionSummary>
      <AccordionDetails className={Styles.details}>
        <div className={Styles.table}>
          <div className={[Styles.row, Styles.headerRow].join(' ')}>
            <IFText className={[Styles.cell, Styles.headerCell].join(' ')}>
              {t('CostBreakdown.Type')}
            </IFText>
            <IFText
              className={[
                Styles.cell,
                Styles.numberCell,
                Styles.headerCell,
              ].join(' ')}
            >
              {t('CostBreakdown.Volume')}
            </IFText>
            <IFText
              className={[
                Styles.cell,
                Styles.numberCell,
                Styles.headerCell,
              ].join(' ')}
            >
              {t('CostBreakdown.ExclVat')}
            </IFText>
            <IFText
              className={[
                Styles.cell,
                Styles.numberCell,
                Styles.headerCell,
              ].join(' ')}
            >
              {t('CostBreakdown.InclVat')}
            </IFText>
          </div>

          {items.map((item, idx) => (
            <div className={Styles.row} key={`${item.type}-${idx}`}>
              <IFText className={Styles.cell}>
                {getTypeLabel(item.type)}
              </IFText>
              <IFText className={[Styles.cell, Styles.numberCell].join(' ')}>
                {item.billedVolume ?? item.volume ?? '-'}
              </IFText>
              <IFText className={[Styles.cell, Styles.numberCell].join(' ')}>
                {formatAmount(item.exclVat)}
              </IFText>
              <IFText className={[Styles.cell, Styles.numberCell].join(' ')}>
                {formatAmount(item.inclVat)}
              </IFText>
            </div>
          ))}

          <div className={Styles.totals}>
            <div className={Styles.totalRow}>
              <IFText className={Styles.totalLabel}>
                {t('CostBreakdown.Subtotal')}
              </IFText>
              <IFText className={Styles.totalValue}>
                {`${formatAmount(subtotal)} ${currency?.code ?? ''}`}
              </IFText>
            </div>
            <div className={Styles.totalRow}>
              <IFText className={Styles.totalLabel}>
                {t('CostBreakdown.Vat')}
              </IFText>
              <IFText className={Styles.totalValue}>
                {`${formatAmount(vatTotal)} ${currency?.code ?? ''}`}
              </IFText>
            </div>
            <div className={Styles.totalRow}>
              <IFText className={[Styles.totalLabel, Styles.grandTotal].join(' ')}>
                {t('CostBreakdown.Total')}
              </IFText>
              <IFText className={[Styles.totalValue, Styles.grandTotal].join(' ')}>
                {`${formatAmount(total)} ${currency?.code ?? ''}`}
              </IFText>
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  )
}

CostBreakdownAccordion.propTypes = {
  costBreakdown: PropTypes.shape({
    items: PropTypes.array,
    dimensionTotals: PropTypes.array,
    subtotal: PropTypes.number,
    vatTotal: PropTypes.number,
    total: PropTypes.number,
  }),
  currency: PropTypes.shape({
    code: PropTypes.string,
    decimalPlaces: PropTypes.number,
  }),
}

export default CostBreakdownAccordion
